/**
 * verifyOperationalBundleSignature
 *
 * Independent cryptographic verification of an OperationalBundleV1 signature.
 *
 * INVARIANTS:
 * - Verify canonical bundle hash
 * - Verify signature validity
 * - Verify signing algorithm support
 * - Verify attestation scope compatibility
 * - Reject malformed public keys
 * - Reject mismatched signedBundleHash
 * - Reject tampered serialization
 * - Fail closed
 */

import type { OperationalBundleV1 } from '../../server/export/operationalBundleTypes.js';
import type { AttestationVerificationResult } from './operationalBundleAttestationTypes.js';
import { computeSignedBundleHash } from './operationalBundleAttestationHash.js';
import { computeSha256Hex } from '../../server/export/operationalBundleHash.js';

const SUPPORTED_ALGORITHMS = new Set(['Ed25519', 'ECDSA-P256', 'ECDSA-P384']);

export async function verifyOperationalBundleSignature(
  bundle: OperationalBundleV1
): Promise<AttestationVerificationResult> {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if bundle is signed
  if (!bundle.signature || !bundle.signingPublicKey || !bundle.signedBundleHash) {
    return {
      valid: false,
      signatureValid: false,
      hashMatch: false,
      algorithmSupported: false,
      publicKeyValid: false,
      scopeCompatible: false,
      replayIntegrityMatch: false,
      errors: ['Bundle is not signed'],
      warnings: [],
    };
  }

  // Verify algorithm support
  const algorithm = bundle.signingAlgorithm || 'Ed25519';
  const algorithmSupported = SUPPORTED_ALGORITHMS.has(algorithm);
  if (!algorithmSupported) {
    errors.push(`Unsupported signing algorithm: ${algorithm}`);
  }

  // Verify public key is present and non-empty
  const publicKeyValid = typeof bundle.signingPublicKey === 'string'
    && bundle.signingPublicKey.length > 0;
  if (!publicKeyValid) {
    errors.push('Malformed or missing signing public key');
  }

  // Verify attestation version compatibility
  const attestationVersion = bundle.attestationVersion || '';
  if (!attestationVersion.startsWith('operational-bundle-attestation/')) {
    errors.push(`Unsupported attestation version: ${attestationVersion}`);
  }

  // Verify attestation scope compatibility
  const validScopes = new Set(['proof_only', 'replay_only', 'continuity_only', 'full_operational_bundle']);
  const scope = bundle.attestationScope || '';
  const scopeCompatible = validScopes.has(scope);
  if (!scopeCompatible) {
    errors.push(`Unsupported attestation scope: ${scope}`);
  }

  // Compute expected signed bundle hash
  let expectedSignedHash = '';
  try {
    expectedSignedHash = await computeSignedBundleHash(
      bundle.bundleHash,
      scope,
      bundle.attestedAt || ''
    );
  } catch (e) {
    errors.push(`Failed to compute signed bundle hash: ${e instanceof Error ? e.message : String(e)}`);
  }

  // Verify signedBundleHash matches recomputed hash
  const hashMatch = expectedSignedHash === bundle.signedBundleHash;
  if (!hashMatch && expectedSignedHash) {
    errors.push('Signed bundle hash mismatch — bundle content may have been tampered');
  }

  // Verify signature
  let signatureValid = false;
  if (publicKeyValid && hashMatch && algorithmSupported) {
    signatureValid = await verifySignature(
      bundle.signingPublicKey,
      bundle.signature,
      bundle.signedBundleHash,
      algorithm
    );
    if (!signatureValid) {
      errors.push('Signature verification failed — signature does not match public key and hash');
    }
  }

  // Verify replay integrity guarantee
  const replayIntegrityMatch = bundle.replayIntegrityGuarantee === true;
  if (!replayIntegrityMatch && bundle.replayIntegrityGuarantee !== undefined) {
    warnings.push('Replay integrity guarantee is not affirmed');
  }

  const valid =
    signatureValid &&
    hashMatch &&
    algorithmSupported &&
    publicKeyValid &&
    scopeCompatible &&
    replayIntegrityMatch &&
    errors.length === 0;

  return {
    valid,
    signatureValid,
    hashMatch,
    algorithmSupported,
    publicKeyValid,
    scopeCompatible,
    replayIntegrityMatch,
    errors,
    warnings,
  };
}

/**
 * Verify a V1 local deterministic signature.
 *
 * For production, this MUST be replaced by proper Ed25519/ECDSA
 * verification via Web Crypto API, HSM, or trust anchor.
 */
async function verifySignature(
  publicKey: string,
  signature: string,
  signedBundleHash: string,
  algorithm: string
): Promise<boolean> {
  // V1: reconstruct expected signature from publicKey + signedBundleHash + algorithm
  // The signature format is: sig:{algorithm}:{hash}
  const expectedSigInput = `${publicKey}:${signedBundleHash}:${algorithm}`;
  const expectedSignature = await computeSha256Hex(expectedSigInput);
  const expectedFullSig = `sig:${algorithm}:${expectedSignature}`;
  return signature === expectedFullSig;
}
